import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Avatar,
  Collapse
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Logout as LogoutIcon,
  Business as BusinessIcon,
  Api as ApiIcon,
  VpnKey as VpnKeyIcon,
  Lock as LockIcon,
  Inventory as InventoryIcon,
  Warehouse as WarehouseIcon,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { Routes, Route, Link as RouterLink, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { checkWildcardPermission } from '../utils/PermissionUtils';
import DashboardContent from './DashboardContent';
import Users from './Users';
import Roles from './Roles';
import Permissions from './Permissions';
import Reports from './Reports';
import Settings from './Settings';
import Account from './Account';
import ClientsPartner from '../fundedhere/components/ClientsPartner';
import PartnerProducts from '../fundedhere/components/PartnerProducts';
import WarehouseRegister from '../fundedhere/components/WarehouseRegister';

const drawerWidth = 240;

const LinkBehavior = React.forwardRef((props, ref) => {
  const { button, onClick, onKeyDown, ...other } = props; // Consume the 'button' and other props that might conflict
  return <RouterLink ref={ref} {...other} />;
});

const Dashboard = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openFundedHere, setOpenFundedHere] = useState(false);
  const navigate = useNavigate();
  const { user } = useUser();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { logout } = useUser();

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleClose();
  };

  const handleFundedHereClick = () => {
    setOpenFundedHere(!openFundedHere);
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    //{ text: 'Reports', icon: <BarChartIcon />, path: '/reports' },
    //{ text: 'Account', icon: <AccountIcon />, path: '/account' },
  ];

  const menuItemsAdmin = [
    { text: 'Users', icon: <PeopleIcon />, path: '/users' },
    { text: 'Roles', icon: <VpnKeyIcon />, path: '/roles' },
    { text: 'Permissions', icon: <LockIcon />, path: '/permissions' },
   // { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          SmartDash Admin
        </Typography>
      </Toolbar>
      <Divider />
      <List>

        {menuItems.map((item) => (
          <ListItem button key={item.text} component={LinkBehavior} to={item.path}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}

        {(() => {
          const hasAdminPermission = checkWildcardPermission(user?.permissions, 'admin:*');
          return hasAdminPermission && (
            <>
              {menuItemsAdmin.map((item) => (
                <ListItem button key={item.text} component={LinkBehavior} to={item.path}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItem>
              ))}
            </>
          );
        })()}

        {(() => {
          const hasFundedHerePermission = checkWildcardPermission(user?.permissions, 'fundedhere:*');
          return hasFundedHerePermission && (
            <>
              <ListItem button onClick={handleFundedHereClick}>
                <ListItemIcon><BusinessIcon /></ListItemIcon>
                <ListItemText primary="FundedHere" />
                {openFundedHere ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse in={openFundedHere} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItem button sx={{ pl: 4 }} component={LinkBehavior} to="/fundedhere/warehouse-register">
                    <ListItemIcon><WarehouseIcon /></ListItemIcon>
                    <ListItemText primary="Warehouses" />
                  </ListItem>
                  <ListItem button sx={{ pl: 4 }} component={LinkBehavior} to="/fundedhere/clients-partner">
                    <ListItemIcon><PeopleIcon /></ListItemIcon>
                    <ListItemText primary="Clients Partner" />
                  </ListItem>
                  <ListItem button sx={{ pl: 4 }} component={LinkBehavior} to="/fundedhere/partner-products">
                    <ListItemIcon><InventoryIcon /></ListItemIcon>
                    <ListItemText primary="Partner Products" />
                  </ListItem>
                </List>
              </Collapse>
            </>
          );
        })()}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgb(253, 197, 31)',
          color: 'black'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Admin Dashboard
          </Typography>

          {/* User profile menu */}
          <div>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, fontSize: '1rem' }}>AU</Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={() => { navigate('/account'); handleClose(); }}>
                My Account
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        <Routes>
          <Route path="/" element={<DashboardContent />} />
          <Route path="/users" element={<Users />} />
          <Route path="/roles" element={<Roles />} />
          <Route path="/permissions" element={<Permissions />} />
          <Route path="/fundedhere/clients-partner" element={<ClientsPartner />} />
          <Route path="/fundedhere/partner-products" element={<PartnerProducts />} />
          <Route path="/fundedhere/warehouse-register" element={<WarehouseRegister />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/account" element={<Account />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default Dashboard;